<!--
  - Copyright (c) 2021. Trade Research Advisory (Pty) Ltd, P.O.Box 336, Cornwall Hill, Irene, 0178
  - #  This file forms part of the Trade-DSM application frontend.
  - #  https://github.com/Tooblippe/trade-dsm-frontend
  -->

<template>
  <div class="pa-5 mr-0">
    <v-layout
      class="pa-0 mt-0 ml-1 align-center "
      :class="{'justify-center': $store.state.app.big_map}"
      row
    >
      <v-img
        :src="'/flags/' + HOME_MARKET_CODE.toLowerCase() + '.svg'"
        alt=""
        class="ml-1 mr-1"
        max-height="50px"
        max-width="50px"
      />

      <h4 class="justify-center align-center">Home market:</h4>
      <h4 class="font-italic justify-center align-center">
        {{ HOME_MARKET }}
      </h4>
    </v-layout>

    <v-layout
      v-if="codeToMarket(SELECTED_MARKETS[0])"
      class="pa-0 mt-0 ml-1 mr-6 align-center"
      :class="{'justify-center': $store.state.app.big_map}"
      row
    >
      <v-img
        :src="'/flags/' + codeToISO(SELECTED_MARKETS[0]).toLowerCase() + '.svg'"
        alt=""
        class="ml-1 mr-1"
        max-height="50px"
        max-width="50px"
      />
      <h4 class="align-center">Target market:</h4>
      <h4 class="font-italic">
        {{ codeToMarket(SELECTED_MARKETS[0]) }}
      </h4>
    </v-layout>

    <template v-if="show_printable" class="ma-0 pa-0 caption">
      <span id="png_placeholder" class="ma-0 pa-0 caption"></span>
    </template>

    <div id="1p1c_map">
      <div class="chartWithOverlay">
        <GChart
          ref="gChart_1p1c"
          :data="create_country_plot_array"
          :events="chartEvents"
          :options="options"
          :resizeDebounce="0"
          :settings="{
            packages: ['geochart'],
            mapsApiKey: mapsApiKey,
            tooltip: {isHtml: true},
          }"
          class="chart pa-0 ma-0"
          type="GeoChart"
          @ready="chartReady"
        />

        <trade-map-image :show_map_ta_overlay="show_map_ta_overlay" />
      </div>
    </div>

    <v-layout
      v-if="show_map_ta_overlay"
      class="pa-0 mt-0 justify-lg-space-around"
      row
      data-html2canvas-ignore
    >
      <v-switch
        v-model="display_mode_var"
        :label="display_mode_var === 'region' ? 'Show bubbles' : 'Highlight countries'"
        class="sm-4 mb-3 ml-lg-5"
        color="green"
        dense
        flat
        inset
        value="region"
        @change="
          options.displayMode = display_mode_var === 'region' ? 'region' : 'markers'
        "
      ></v-switch>
    </v-layout>
    <v-layout
      v-if="show_map_ta_overlay"
      class="pa-0 mt-0 justify-center align-center"
      row
      data-html2canvas-ignore
    >
      <v-toolbar-title class="ml-2">
        <v-tooltip max-width="200" top>
          <template v-slot:activator="{on}">
            <v-btn
              v-on="on"
              :disabled="options.region === 'world'"
              class="sm-4 ml-3 mb-3 v-btn--outlined green lighten-3 pa-4 pl-1 pr-1"
              small
              @click="options.region = 'world'"
              >World
            </v-btn>
          </template>
          <span>Click to select world map.</span>
        </v-tooltip>
      </v-toolbar-title>

      <v-toolbar-title class="ml-2">
        <v-tooltip max-width="200" top>
          <template v-slot:activator="{on}">
            <v-btn
              v-on="on"
              :disabled="
                typeof SELECTED_MARKETS[0] !== 'undefined'
                  ? options.region ===
                    pad_with_zeroes(codeToMain_Region(SELECTED_MARKETS[0]), 3)
                  : true
              "
              class="sm-4 ml-3 mb-3 v-btn--outlined green lighten-3 pa-4 pl-1 pr-1"
              small
              @click="
                options.region = pad_with_zeroes(
                  codeToMain_Region(SELECTED_MARKETS[0]),
                  3,
                )
              "
              >Target Market<br />
              Region
            </v-btn>
          </template>

          <span
            >Click to zoom into the target market ({{
              codeToMarket(SELECTED_MARKETS[0])
            }}) region.</span
          >
        </v-tooltip>
      </v-toolbar-title>

      <v-toolbar-title class="ml-2">
        <v-tooltip max-width="200" top>
          <template v-slot:activator="{on}">
            <v-btn
              v-on="on"
              :disabled="
                typeof SELECTED_MARKETS[0] !== 'undefined'
                  ? options.region ===
                    pad_with_zeroes(codeToSub_Region_Code(SELECTED_MARKETS[0]), 3)
                  : true
              "
              class="sm-4 ml-3 mb-3 v-btn--outlined green lighten-3 pa-4 pl-1 pr-1"
              small
              @click="
                options.region = pad_with_zeroes(
                  codeToSub_Region_Code(SELECTED_MARKETS[0]),
                  3,
                )
              "
              >Target Market<br />Sub Region
            </v-btn>
          </template>
          <span
            >Click to zoom into the target market ({{
              codeToMarket(SELECTED_MARKETS[0])
            }}) subregion.</span
          ></v-tooltip
        >
      </v-toolbar-title>
      <help-link help_id="4" class="ml-12" />
    </v-layout>
  </div>
</template>

<script>
import {GChart} from 'vue-google-charts';
import vFmt from '@/components/application-level/mixins/vFmt';
import {mapGetters} from 'vuex';
import TradeMapImage from '@/components/trade-dsm-application/result-components/helpers/trade-map-image';
import helpLink from '@/components/application-level/help-system/help-link';

export default {
  name: 'worldMap',
  mixins: [vFmt],
  components: {
    GChart,
    TradeMapImage,
    helpLink,
  },

  mounted() {
    this.options.region = this.pad_with_zeroes(this.homeCountry['Main_Region_Code'], 3);
  },

  data() {
    return {
      show_printable: true,
      show_map_ta_overlay: false,

      mapsApiKey: 'AIzaSyDNbYELN5uXbJpmqIOKBC7AO0aZeazfnM8',
      display_mode_var: 'markers',
      options: {
        region: 'world',
        legend: 'none',
        displayMode: 'markers', //region
        colorAxis: {
          minValue: 0,

          colors: ['blue', '#00ff00', 'red'],
        },
        // sizeAxis: {minValue: 50, maxValue: 100},
        backgroundColor: {
          strokeWidth: '0' /* control the countries borders width */,
          stroke: 'lightgray',
        },
        datalessRegionColor: '#cadcf5',

        height: this.$store.state.app.big_map ? 400 : 360, //360
        forceFrame: true,
        enableRegionInteractivity: true,
        magnifyingGlass: {enable: true, zoomFactor: 7.5},
        tooltip: {
          isHtml: true,
          showTitle: false,
        },
      },
      markers: {
        stroke: '#00ff00',
      },
      chartEvents: {
        select: async () => {
          // const chart = this.$refs.gChart.chartObject.getSelection();
          //
          // // get reference to data
          // const chart_id = chart[0]['row'] + 1;
          //
          // // get data point 0
          // // if region then this point is the country ISO2_Alpha
          // // if not region then this point is the CoOrdinate_X
          // const country = this.create_country_plot_array[chart_id][0];
          //
          // let country_code = this.$store.state.tradeDSM.quick_selectors[0].items.filter(
          //   v => v.C_Name === country,
          // )[0]['C_Code'];
          //
          // this.$store.state.tradeDSM.dsm_filter_setttings.Filters.META.selections.markets[
          //   'C_Code'
          // ] = [];
          // this.$store.state.tradeDSM.dsm_filter_setttings.Filters.META.selections.markets[
          //   'C_Code'
          // ].push(country_code);
          //
          // this.update_results_cube()
        },
      },
    };
  },

  watch: {
    targetCountry: function(v) {
      this.options['region'] = this.pad_with_zeroes(v['Main_Region_Code'], 3);
    },
    homeCountry: function(v) {
      this.options['region'] = this.pad_with_zeroes(v['Main_Region_Code'], 3);
    },
    top6: function() {
      this.options['region'] = 'world';
    },
  },

  computed: {
    ...mapGetters(['app_data', 'selectionCounts']),

    create_country_plot_array() {
      return [
        ['Country', 'Value', 'Size', {role: 'tooltip', p: {html: true}}],

        [
          // name
          this.$store.state.app.selectedHomeCountry,
          // color
          1,
          //value
          // (homeCountryExports / app_data.value_mulitplier['divider'] / 1000) *
          //   app_data.exchange_rate['TW_FX_Rate']
          Math.max(...this.top6['y']) / 3 || 5,

          //text
          `<p style = 'white-space: nowrap;'> ${
            this.$store.getters.dsmFilterSettings['Destination_Country']
              ? 'Home Market </br> Total exports to ' +
                this.$store.getters.dsmFilterSettings['Destination_Country'] +
                ' ' +
                (
                  ((this.homeCountryExports ? this.homeCountryExports : 0) /
                    this.app_data.value_mulitplier['divider'] /
                    1000) *
                  this.app_data.exchange_rate['TW_FX_Rate']
                ).toFixed(2) +
                this.app_data.value_mulitplier['prefix'] +
                ' ' +
                this.app_data.exchange_rate['Currency'].slice(0, 3) +
                '<br>Share of imports into target market [rank]: ' +
                this.rank +
                ' out of ' +
                this.total_exporters
              : 'Home Market <br>' +
                this.codeToMarket(
                  this.$store.state.tradeDSM.dsm_filter_setttings.Filters.META
                    .home_market,
                ) +
                '(' +
                this.$store.state.tradeDSM.dsm_filter_setttings.Filters.META.home_market +
                ')'
          }</p>`,
        ],

        //destination market -> need to replaced with dynamic code

        [
          this.codeToMarket(this.SELECTED_MARKETS[0]),
          2,
          // (selectionCounts.results['trade_value'] /
          //   app_data.value_mulitplier['divider']) *
          //   app_data.exchange_rate['TW_FX_Rate'],
          Math.max(...this.top6['y']) / 3 || 5,

          this.untapped_potential,
        ],

        [
          this.top6_countries[0],
          5,
          this.top6['y'][0],

          `<p style = 'white-space: nowrap;'>
          ${this.top6_countries[0]}
          (${this.marketToCode(this.top6_countries[0])})</br>
          ${this.app_data.exchange_rate['Currency'].slice(0, 3)} ${this.top6[
            'y'
          ][0].toFixed(2)} ${this.app_data.value_mulitplier['prefix']}<br> Rank ${1}</p>`,
        ],
        [
          this.top6_countries[1],
          5,
          this.top6['y'][1],

          `<p style = 'white-space: nowrap;'>
           ${this.top6_countries[1]}
           (${this.marketToCode(this.top6_countries[1])})</br>
          ${this.app_data.exchange_rate['Currency'].slice(0, 3)} ${this.top6[
            'y'
          ][1].toFixed(2)} ${this.app_data.value_mulitplier['prefix']}<br> Rank ${2}</p>`,
        ],
        [
          this.top6_countries[2],
          5,
          this.top6['y'][2],

          `<p style = 'white-space: nowrap;'>
               ${this.top6_countries[2]}
               (${this.marketToCode(this.top6_countries[2])})</br>
            ${this.app_data.exchange_rate['Currency'].slice(0, 3)} ${this.top6[
            'y'
          ][2].toFixed(2)} ${this.app_data.value_mulitplier['prefix']}<br> Rank ${3}</p>`,
        ],
        [
          this.top6_countries[3],
          5,
          this.top6['y'][3],

          `<p style = 'white-space: nowrap;'>
        ${this.top6_countries[3]}
        (${this.marketToCode(this.top6_countries[3])})</br>
        ${this.app_data.exchange_rate['Currency'].slice(0, 3)} ${this.top6[
            'y'
          ][3].toFixed(2)} ${this.app_data.value_mulitplier['prefix']}<br> Rank ${4}</p>`,
        ],
        [
          this.top6_countries[4],
          5,
          this.top6['y'][4],

          `<p style = 'white-space: nowrap;'>
        ${this.top6_countries[4]}
        (${this.marketToCode(this.top6_countries[4])})</br>
        ${this.app_data.exchange_rate['Currency'].slice(0, 3)} ${this.top6[
            'y'
          ][4].toFixed(2)} ${this.app_data.value_mulitplier['prefix']}<br> Rank ${5}</p>`,
        ],
        [
          this.top6_countries[5],
          5,
          this.top6['y'][5],

          `<p style = 'white-space: nowrap;'>
        ${this.top6_countries[5]}
        (${this.marketToCode(this.top6_countries[5])})</br>
        ${this.app_data.exchange_rate['Currency'].slice(0, 3)} ${this.top6[
            'y'
          ][5].toFixed(2)} ${this.app_data.value_mulitplier['prefix']}<br> Rank ${6}</p>`,
        ],

        // [$store.getters.dsmFilterSettings.results.top6_countries[6], 10000],
      ];
    },

    HOME_MARKET() {
      return this.$store.state.app.selectedHomeCountry;
    },
    HOME_MARKET_CODE() {
      const val = this.$store.getters.countriesSelectorList.length;
      // check if there are countries to choose from
      if (val > 0) {
        //
        return this.$store.getters.countriesSelectorList
          .filter(
            country => country['C_Name'] === this.$store.state.app.selectedHomeCountry,
          )
          .map(v => v['C_Code_ISO_Alpha_2'])[0];
      } else {
        return '';
      }
    },
    SELECTED_MARKETS() {
      return this.$store.state.tradeDSM.dsm_filter_setttings.Filters.META.selections
        .markets.C_Code;
    },
    total_exporters() {
      try {
        return this.$store.getters.getDsmAppState.dsm_filter_setttings.results
          .dsmresult[0]['Nr_Importers'];
      } catch (e) {
        return '0';
      }
    },
    rank() {
      try {
        return this.$store.getters.getDsmAppState.dsm_filter_setttings.results
          .dsmresult[0]['HM_Rank'] >= 0
          ? this.$store.getters.getDsmAppState.dsm_filter_setttings.results.dsmresult[0][
              'HM_Rank'
            ]
          : 0;
      } catch (e) {
        return '0';
      }
    },

    homeCountryExports: function() {
      // return this.$store.getters.top6PlotData["x"];
      let cArray = [];
      for (const m of this.$store.state.tradeDSM.dsm_filter_setttings.results['top6']) {
        for (const c of this.$store.state.tradeDSM.quick_selectors[0].items) {
          // console.log("Selected", m["Supplying_Market"]);
          if (c['C_Code'] === m['Supplying_Market']) {
            // console.log(c['C_Name'], m['C-Code']);
            cArray.push(c['C_Name']);
          }
        }
      }
      cArray[7] = 'Rest';
      return this.$store.getters.top6PlotData['y'][
        cArray.indexOf(this.$store.state.app.selectedHomeCountry)
      ];
    },

    top6_countries() {
      let a_len = this.$store.getters.dsmFilterSettings.results.top6_countries.length;
      let arr = [...this.$store.getters.dsmFilterSettings.results.top6_countries];
      if (a_len < 6) {
        for (let i = 0; i < 6 - a_len; i++) {
          arr.push('');
        }
        return arr;
      } else return arr;
    },
    top6() {
      let x = [...this.$store.getters.top6PlotData['x']];
      let y = [
        ...this.$store.getters.top6PlotData['y'].map(
          v =>
            (v / this.app_data.value_mulitplier['divider'] / 1000) *
            this.app_data.exchange_rate['TW_FX_Rate'],
        ),
      ];
      let y_len = y.length;
      // console.log('building array y....');
      // console.log(y);
      // console.log(y.length);
      if (y.length < 6) {
        // console.log('in loop');
        for (let i = 0; i < 6 - y_len; i++) {
          y.push(0);
          x.push('');
          // console.log('building array....' + i);
        }
        // console.log('=============');
        // console.log(x);
        // console.log(y);
        // console.log('=============');

        return {x: x, y: y};
      } else {
        // console.log('=============');
        // console.log(x);
        // console.log(y);
        // console.log('=============');
        return {x: x, y: y};
      }
    },
    homeCountry() {
      // return this.$store.getters.getDsmAppState.dsm_filter_setttings.Country_Selection;
      return this.$store.getters.countriesSelectorList.filter(
        country => country['C_Name'] === this.$store.state.app.selectedHomeCountry,
      )[0];
    },
    targetCountry() {
      return this.$store.getters.countriesSelectorList.filter(
        country =>
          country['C_Name'] ===
          this.$store.getters.dsmFilterSettings['Destination_Country'],
      )[0];
    },
    productAndCountrySelected() {
      return (
        this.$store.state.tradeDSM.dsm_filter_setttings['Destination_Market'].length >
          0 && this.$store.state.tradeDSM.dsm_filter_setttings['HS6_Code'].length > 0
      );
    },

    selectedCountry() {
      let val = this.$store.getters.countriesSelectorList.length;
      // check if there are countries to select from
      if (val > 0) {
        // check if a destination country was wet
        if (this.$store.getters.dsmFilterSettings['Destination_Country'].length !== 0) {
          return this.$store.getters.countriesSelectorList
            .filter(
              country =>
                country['C_Name'] ===
                this.$store.getters.dsmFilterSettings['Destination_Country'],
            )
            .map(v => v['C_Code_ISO_Alpha_2'])[0];
        } else {
          return '';
        }
      } else return '';
    },

    untapped_potential() {
      try {
        return (
          "<p style = 'white-space: nowrap;'>" +
          this.codeToMarket(this.SELECTED_MARKETS[0]) +
          ` (${this.SELECTED_MARKETS[0]})` +
          '<br>Target Market <br>Untapped Potential: <br>' +
          this.app_data.exchange_rate['Currency'].slice(0, 3) +
          ' ' +
          this.vFmt(
            (this.selectionCounts.results['trade_value'] /
              this.app_data.value_mulitplier['divider']) *
              this.app_data.exchange_rate['TW_FX_Rate'],
            2,
          ) +
          '' +
          this.app_data.value_mulitplier['prefix'] +
          '</p>'
        );
      } catch (e) {
        return '';
      }
    },
  },

  methods: {
    chartReady: function() {
      // alert(chart);
      // alert(google)
      // ;
      // google.visualization.events.addListener(chart, 'ready', function() {
      //   try {
      //     document.getElementById('png_placeholder').outerHTML =
      //       '<a class="caption ma-0 pa-0" href="' +
      //       chart.getImageURI() +
      //       '" target="_blank">Printable version</a>';
      //
      //     // this.image_uri = chart.getImageURI();
      //   } catch (e) {
      //     console.log('error in png');
      //     console.log(e);
      //   }
      // });

      this.show_map_ta_overlay = true;
    },
    // showButtons: () => {
    //   if (this.homeCountry['Main_Region_Code'] !== undefined) {
    //     return (
    //       this.homeCountry['Main_Region_Code'] !== '-' &&
    //       this.homeCountry['Main_Region_Code']
    //     );
    //   } else {
    //     return false;
    //   }
    // },
    pad_with_zeroes: (number, length) => {
      try {
        let my_string = '' + number;
        // console.log('number set' + number);
        while (my_string.length < length) {
          my_string = '0' + my_string;
        }
        return my_string;
      } catch {
        return 'world';
      }
    },
  },
};
</script>

<style scoped>
p {
  background-color: cyan;
}

.google-visualization-tooltip-item {
  white-space: nowrap;
}

.chartWithOverlay {
  position: relative;
  /*width: 700px;*/
}

.overlay {
  /*width: 200px;*/
  /*height: 200px;*/
  position: absolute;
  color: rgb(0, 102, 153);
  /*top: 40px; !* chartArea top  *!*/
  left: 40%;

  bottom: 50px;
}

circle {
  stroke-width: 30px !important;
}
</style>
